import React, { useState } from "react";
import iziToast from "izitoast";
import { RefreshCw, Search, Sparkles, BrainCircuit, Send } from "lucide-react";
import PageWrapper from "components/PageWrapper";
import { getBomAiSearchAPI, refreshBomAiTokenAPI } from "actions/bomActions";
import { getProductPriceSlabs, filterProductsWithSlab, getCurrencySymbol } from "utils/searchUtils";
import LoadingComponent from "components/LoadingComponent";
import { formatDate, sortByDate } from "utils/helper";
import { normalizeBomAiProduct } from "utils/bomUtils";
import SamiSpin from "components/SamiSpin";

const stockMeta = (status) => {
  const v = (status || "").toLowerCase();
  if (v.includes("out")) return { pill: "bg-s360-danger-bg text-[#700A14]", dot: "bg-s360-danger" };
  if (v.includes("low")) return { pill: "bg-s360-warning-bg text-s360-warning-ink", dot: "bg-s360-warning" };
  return { pill: "bg-s360-success-bg text-s360-success-ink", dot: "bg-s360-success" };
};

const prettyStatus = (s) => (s ? String(s).replace(/_/g, " ") : "—");

const BomAIPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [searchData, setSearchData] = useState({
    products: [],
    groupArray: [],
  });
  const { groupArray } = searchData;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    if (!searchValue?.trim()) {
      setErrorMessage("Search field cannot be empty");
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await getBomAiSearchAPI(searchValue);
      if (response.status) {
        const sortedItems = sortByDate(response.data?.items ?? [], "updated_at");
        const normalized = sortedItems?.length > 0 ? sortedItems?.map(normalizeBomAiProduct) : [];
        const groupArray = groupProducts(normalized);
        setSearchData({ ...searchData, groupArray });
      } else {
        setErrorMessage(response.message);
      }
    } catch (error) {
      console.log({ error });
      setErrorMessage(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const groupProducts = (productArray) => {
    const groups = [...new Set(productArray.map((item) => item.part_number))];
    const groupArray = [];
    for (let j = 0; j < groups.length; j++) {
      const groupProductsList = productArray.filter((product) => product.part_number === groups[j]);
      const filteredProducts = filterProductsWithSlab({
        products: groupProductsList,
      });
      const groupData = {
        name: groups[j],
        priceSlabs: getProductPriceSlabs(filteredProducts),
        products: filteredProducts,
      };
      groupArray.push(groupData);
    }
    return groupArray;
  };

  const handleRefreshToken = async () => {
    try {
      setIsLoading(true);
      setErrorMessage("");
      const response = await refreshBomAiTokenAPI();
      if (response.status) {
        iziToast.success({
          title: "Success:",
          message: "Token refreshed successfully",
          position: "topCenter",
        });
      } else {
        setErrorMessage(response.message);
      }
    } catch (error) {
      console.log({ error });
    } finally {
      setIsLoading(false);
    }
  };

  // Best product id per slab min_qty + overall best price for a group.
  const computeBest = (group) => {
    const bestBySlab = {};
    group.priceSlabs.forEach((slab) => {
      let lo = Infinity;
      let owner = null;
      group.products.forEach((p, idx) => {
        const s = p.price?.price_list?.find((l) => l.min_qty === slab.min_qty);
        if (s && Number(s.price) < lo) {
          lo = Number(s.price);
          owner = idx;
        }
      });
      bestBySlab[slab.min_qty] = owner;
    });

    let overall = { price: Infinity, supplier: null, qk: null, currency: "" };
    group.products.forEach((p) => {
      (p.price?.price_list || []).forEach((s) => {
        if (Number(s.price) < overall.price) {
          overall = {
            price: Number(s.price),
            supplier: p.supplier_name,
            qk: s.min_qty,
            currency: s.currency ?? p.currency,
          };
        }
      });
    });
    return { bestBySlab, overall };
  };

  return (
    <PageWrapper>
      <div className="flex flex-col gap-4">
        {/* Page head */}
        <div className="flex flex-col gap-1.5">
          <h1 className="text-[28px] leading-[34px] font-bold tracking-[-0.015em] text-s360-ink">
            BOM AI
          </h1>
          <span className="text-sm text-s360-fg3">
            AI-aggregated supplier intelligence across global brokers, EMS partners, and
            gray-market sources.
          </span>
        </div>

        {/* AI hero with embedded search */}
        <div className="rounded-md shadow-sm overflow-hidden bg-gradient-to-br from-s360-violet to-s360-violet-900 text-white p-5 flex flex-col md:flex-row md:items-center gap-4">
          <span className="w-12 h-12 rounded-lg bg-white/15 inline-flex items-center justify-center shrink-0">
            <BrainCircuit className="w-6 h-6" />
          </span>
          <div className="flex flex-col min-w-0 flex-1">
            <h2 className="text-lg font-bold leading-tight">Find unconventional sources</h2>
            <span className="text-[13px] text-white/80">
              Searches our broker network for parts your authorized distributors don't stock.
            </span>
          </div>
          <form onSubmit={handleSubmit} className="flex items-center gap-2 w-full md:w-auto">
            <div className="flex items-center gap-2 h-10 px-3 bg-white rounded-md flex-1 md:w-[280px] md:flex-none">
              <Search className="w-4 h-4 text-s360-fg3" />
              <input
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder="Part number…"
                className="flex-1 bg-transparent outline-none text-sm text-s360-fg1 placeholder:text-s360-fg-disabled"
              />
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md bg-s360-teal text-s360-ink text-sm font-bold hover:bg-[#5EE7DA] disabled:opacity-60"
            >
              {isSubmitting ? (
                <SamiSpin className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              Search
            </button>
            <button
              type="button"
              onClick={handleRefreshToken}
              disabled={isLoading}
              title="Refresh OAuth token"
              className="inline-flex items-center justify-center w-10 h-10 rounded-md bg-white/15 text-white hover:bg-white/25 disabled:opacity-60"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`} />
            </button>
          </form>
        </div>

        {errorMessage && (
          <div className="text-s360-danger bg-s360-danger-bg border border-[#F1BBC0] rounded-md px-3 py-2.5 text-sm text-center">
            {errorMessage}
          </div>
        )}

        {isSubmitting ? (
          <LoadingComponent message="Searching for products..." />
        ) : (
          groupArray.map((group, groupIndex) => {
            const { bestBySlab, overall } = computeBest(group);
            const brand = group.products[0]?.brand;
            return (
              <div
                key={groupIndex}
                className="bg-white rounded-md shadow-sm flex flex-col overflow-hidden"
              >
                {/* Group head */}
                <div className="px-[18px] py-3.5 border-b border-s360-stroke1 flex items-center gap-3 flex-wrap">
                  <span className="w-9 h-9 rounded-lg bg-s360-teal-50 text-s360-teal inline-flex items-center justify-center shrink-0">
                    <Sparkles className="w-5 h-5" />
                  </span>
                  <div className="flex flex-col min-w-0">
                    <span className="font-mono text-[15px] font-bold text-s360-ink">
                      {group.name}
                    </span>
                    {brand && <span className="text-xs text-s360-fg3">{brand}</span>}
                  </div>
                  {overall.supplier && (
                    <div className="ml-auto flex flex-col items-end">
                      <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-s360-fg3">
                        Best price
                      </span>
                      <span className="font-mono text-lg font-bold text-s360-success">
                        {getCurrencySymbol(overall.currency)}
                        {overall.price.toFixed(2)}
                      </span>
                      <span className="text-[11px] text-s360-fg3">
                        @ {overall.qk}+ from {overall.supplier}
                      </span>
                    </div>
                  )}
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr>
                        {["Supplier", "Brand", "Stock", "MOQ / MPQ", "Updated"].map((h) => (
                          <th
                            key={h}
                            className="text-left py-3 px-3.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-s360-fg3 bg-s360-bg2 border-b border-s360-stroke1 whitespace-nowrap"
                          >
                            {h}
                          </th>
                        ))}
                        {group.priceSlabs.length > 0 ? (
                          group.priceSlabs.map((slab, i) => (
                            <th
                              key={i}
                              className="text-right py-3 px-3.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-s360-fg3 bg-s360-bg2 border-b border-s360-stroke1 whitespace-nowrap min-w-[80px]"
                            >
                              {Number(slab.min_qty).toLocaleString()}+
                            </th>
                          ))
                        ) : (
                          <th className="text-right py-3 px-3.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-s360-fg3 bg-s360-bg2 border-b border-s360-stroke1">
                            No price data
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {group.products.map((product, index) => {
                        const isTopPick = overall.supplier && product.supplier_name === overall.supplier;
                        const sm = stockMeta(product?.qty?.stock_status);
                        return (
                          <tr
                            key={index}
                            className={`border-b border-s360-bg2 last:border-0 ${
                              isTopPick ? "bg-[#F2FBF5]" : "hover:bg-s360-stripe"
                            }`}
                          >
                            <td className="py-3 px-3.5">
                              <span className="inline-flex items-center gap-2">
                                <span className="w-[26px] h-[26px] rounded-[5px] bg-s360-violet inline-flex items-center justify-center text-[10px] font-bold text-white">
                                  {(product.supplier_name || "?").slice(0, 2).toUpperCase()}
                                </span>
                                <span className="flex flex-col">
                                  <span className="text-[13px] font-semibold text-s360-fg1">
                                    {product.supplier_name}
                                  </span>
                                  {isTopPick && (
                                    <span className="text-[10px] font-semibold text-s360-success">
                                      AI's top pick
                                    </span>
                                  )}
                                </span>
                              </span>
                            </td>
                            <td className="py-3 px-3.5 text-[13px] text-s360-fg2">{product.brand}</td>
                            <td className="py-3 px-3.5">
                              <span
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold ${sm.pill}`}
                              >
                                <span className={`w-1.5 h-1.5 rounded-full ${sm.dot}`} />
                                {product?.qty?.qty != null
                                  ? `${Number(product.qty.qty).toLocaleString()} · `
                                  : ""}
                                {prettyStatus(product?.qty?.stock_status)}
                              </span>
                            </td>
                            <td className="py-3 px-3.5 font-mono text-xs text-s360-fg2">
                              <strong className="text-s360-fg1">{product.moq ?? "-"}</strong> /{" "}
                              {product.mpq ?? "-"}
                            </td>
                            <td className="py-3 px-3.5 font-mono text-xs text-s360-fg3 whitespace-nowrap">
                              {formatDate(product.updated_at)}
                            </td>
                            {group.priceSlabs.length > 0 ? (
                              group.priceSlabs.map((slab, slabIndex) => {
                                const findSlab = product.price?.price_list?.find(
                                  (list) => list.min_qty === slab.min_qty,
                                );
                                const currency = findSlab?.currency ?? product.currency;
                                const isBest = bestBySlab[slab.min_qty] === index;
                                return (
                                  <td
                                    key={slabIndex}
                                    className={`py-3 px-3.5 text-right font-mono text-[13px] tabular-nums ${
                                      isBest
                                        ? "bg-s360-success-bg font-bold text-s360-success-ink"
                                        : "text-s360-fg1"
                                    }`}
                                  >
                                    {findSlab ? (
                                      <>
                                        {getCurrencySymbol(currency)}
                                        {findSlab.price}
                                      </>
                                    ) : (
                                      <span className="text-s360-fg-disabled">—</span>
                                    )}
                                  </td>
                                );
                              })
                            ) : (
                              <td className="py-3 px-3.5 text-right text-s360-fg-disabled">—</td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })
        )}
      </div>
    </PageWrapper>
  );
};

export default BomAIPage;
